import { Section, Sub, Card, Note, Warn, Fields, New } from '../../../components/docs/Blocks';

export default function SavingChanges() {
  return (
    <Section
      id="saving"
      title="Saving changes"
      lead="Nothing you do in Toolchest touches the world until you press Save. Everything is staged in memory, listed, reviewable and undoable up to that point — and checked afterwards."
    >
      <Sub id="saving-staging" title="The action bar">
        <Card>
          <p>
            The bar along the bottom of the window is present whenever a world is open
            and always says the same thing: how many changes are waiting, and which
            screens they came from.
          </p>
          <Fields
            items={[
              ['No unsaved changes', 'Nothing is staged. Save and Revert are inert.'],
              ['n unsaved changes', 'What a save would write, with the sections involved.'],
              ['Review', 'Opens the list of individual changes.'],
              ['Revert', 'Discards every staged change at once. The world was never touched, so there is nothing to undo on disk.'],
              ['Save Changes', 'Writes everything.'],
            ]}
          />
          <p>
            Staging is why you can move between screens freely. Rename a player on one
            tab, prune chunks on another, edit a villager’s trades on a third, and it
            is one save.
          </p>
        </Card>
      </Sub>

      <Sub id="saving-review" title={<>Reviewing before you save <New /></>}>
        <Card>
          <p>
            <strong>Review</strong> lists every staged change individually — what it
            is, which screen it came from, and what the value was before. Each one can
            be undone on its own without discarding the rest, or you can discard the
            lot.
          </p>
          <p>
            This is the answer to coming back to the program after a break and not
            being sure what you had changed.
          </p>
        </Card>
      </Sub>

      <Sub id="saving-what-happens" title="What happens when you save">
        <Card>
          <ol className="space-y-2 list-decimal list-inside">
            <li>
              A full backup of the world is taken, if this is the first save of the
              session and your backup policy allows it.
            </li>
            <li>A restore point is captured, so a save that fails midway can be rolled back.</li>
            <li>level.dat, the loose files and the world database are written.</li>
            <li>
              Every change is read back out of the world and compared with what was
              asked for.
            </li>
            <li>
              Toolchest reports what it wrote and how long it took, and confirms that
              the changes read back correctly.
            </li>
          </ol>
          <Note>
            If the save fails during the file or level.dat phase, the world is rolled
            back automatically and you are told so — you are not left with a
            half-written world. If the write completes but the check afterwards finds
            something unexpected, Toolchest reports exactly what and offers to undo the
            save.
          </Note>
        </Card>
      </Sub>

      <Sub id="saving-after" title="After saving">
        <Card>
          <p>
            Toolchest keeps holding the world open after a save, so Minecraft still
            cannot load it. The confirmation says so and offers to close the world
            there and then.
          </p>
          <Warn>
            Closing the window with changes staged prompts first and tells you how many
            would be lost. Staged edits live only in memory, so discarding is exactly
            what would happen otherwise.
          </Warn>
        </Card>
      </Sub>
    </Section>
  );
}
